import React, { useState, useEffect, useRef } from "react";
import {
  StepCard,
  StepTitle,
  StepSubtitle,
  FormGroup,
  InputField,
  PhoneInputGroup,
  NavigationButtonsRow,
  ActionButton,
} from "../registerStyles";
import {
  solicitarCodigoWhatsApp,
  validarCodigoWhatsApp,
} from "../../../services/api/verificacionOtp";
import { useToasts } from "react-toast-notifications";

export const StepPersonalInfo = ({
  formData,
  setFormData,
  onNext,
  onBack,
}) => {
  const { addToast } = useToasts();

  const [codigoEnviado, setCodigoEnviado] = useState(false);
  const [otpInput, setOtpInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [intentosRestantes, setIntentosRestantes] = useState(3);
  const timerRef = useRef(null);

  const telefono = formData.telefono || "";
  const isPhoneValid = /^9\d{8}$/.test(telefono.trim());
  const isNameFilled = formData.nombres && formData.nombres.trim().length > 0;
  const isPhoneVerified = !!formData.telefonoVerificado;

  // Temporizador para el cooldown
  useEffect(() => {
    if (cooldown > 0) {
      timerRef.current = setInterval(() => {
        setCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [cooldown]);

  const handlePhoneChange = (e) => {
    const val = e.target.value.replace(/[^0-9]/g, "").slice(0, 9);
    // Si cambia el teléfono y ya estaba verificado, se invalida
    setFormData({
      ...formData,
      telefono: val,
      telefonoVerificado: false,
      fechaVerificacionTelefono: null,
    });
    if (codigoEnviado) {
      setCodigoEnviado(false);
      setOtpInput("");
    }
  };

  const handleEnviarCodigo = async () => {
    if (!isPhoneValid) {
      addToast("Ingrese un número celular válido de Perú (9 dígitos empezando con 9).", {
        appearance: "warning",
      });
      return;
    }

    try {
      setIsSending(true);
      const res = await solicitarCodigoWhatsApp(telefono);
      setCodigoEnviado(true);
      setCooldown(res.cooldownSeconds || 60);
      setIntentosRestantes(3);
      setOtpInput("");
      addToast("¡Código enviado a tu WhatsApp!", { appearance: "success" });
    } catch (err) {
      addToast(err.message || "Error al enviar el código de WhatsApp.", {
        appearance: "error",
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleVerificarCodigo = async () => {
    if (!otpInput || otpInput.trim().length !== 6) {
      addToast("Ingrese el código completo de 6 dígitos.", {
        appearance: "warning",
      });
      return;
    }

    try {
      setIsVerifying(true);
      await validarCodigoWhatsApp(telefono, otpInput);
      setFormData({
        ...formData,
        telefonoVerificado: true,
        fechaVerificacionTelefono: new Date().toISOString(),
      });
      setCodigoEnviado(false);
      addToast("¡Teléfono verificado correctamente!", { appearance: "success" });
    } catch (err) {
      setIntentosRestantes((prev) => Math.max(0, prev - 1));
      addToast(err.message || "Código incorrecto o expirado.", {
        appearance: "error",
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const canProceed = isNameFilled && isPhoneValid && isPhoneVerified;

  return (
    <StepCard>
      <StepTitle>¡Hola, {formData.nombres || "Doctor(a)"}!</StepTitle>
      <StepSubtitle>
        Vamos a configurar tu consultorio dental. Completa y valida tus datos de contacto.
      </StepSubtitle>

      {/* Cuenta Google conectada */}
      <div
        style={{
          background: "#f0f8ff",
          padding: "0.75rem 1.25rem",
          borderRadius: "12px",
          border: "1.5px solid #67D2DC",
          marginBottom: "1.25rem",
          display: "flex",
          alignItems: "center",
          gap: "0.75rem",
        }}
      >
        <span style={{ color: "#0c8de3", fontSize: "1.1rem", fontWeight: "bold" }}>
          ✓
        </span>
        <div>
          <p style={{ margin: 0, fontWeight: 600, fontSize: "0.85rem", color: "#0C234C" }}>
            Cuenta Google conectada
          </p>
          <p style={{ margin: 0, fontSize: "0.8rem", color: "#64748b" }}>
            {formData.email}
          </p>
        </div>
      </div>

      {/* Nombre y Apellidos */}
      <FormGroup>
        <label>Nombre y Apellidos *</label>
        <InputField
          type="text"
          placeholder="Nombres completos"
          value={formData.nombres}
          onChange={(e) => setFormData({ ...formData, nombres: e.target.value })}
          required
        />
      </FormGroup>

      {/* Teléfono Celular con validación Perú */}
      <FormGroup>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.35rem" }}>
          <label style={{ margin: 0 }}>Teléfono Celular WhatsApp *</label>
          {isPhoneVerified && (
            <span style={{ fontSize: "0.75rem", fontWeight: 700, color: "#059669", display: "flex", alignItems: "center", gap: "0.25rem" }}>
              ✓ Verificado
            </span>
          )}
        </div>

        <PhoneInputGroup>
          <div className="country-code">
            <span>🇵🇪</span>
            <span>+51</span>
          </div>
          <InputField
            type="tel"
            placeholder="987 654 321"
            value={formData.telefono}
            maxLength={9}
            disabled={isPhoneVerified}
            onChange={handlePhoneChange}
            required
            style={{
              borderColor: isPhoneVerified ? "#10b981" : (telefono && !isPhoneValid ? "#f87171" : "#cbd5e1"),
              background: isPhoneVerified ? "#f0fdf4" : "#ffffff",
            }}
          />
        </PhoneInputGroup>

        {telefono && !isPhoneValid && (
          <p style={{ margin: "0.35rem 0 0 0", fontSize: "0.75rem", color: "#dc2626" }}>
            El número celular debe tener 9 dígitos y comenzar con 9.
          </p>
        )}
      </FormGroup>

      {/* Bloque de Verificación WhatsApp */}
      {!isPhoneVerified ? (
        <div
          style={{
            background: "#f8fafc",
            border: "1.5px dashed #cbd5e1",
            borderRadius: "12px",
            padding: "1rem",
            marginBottom: "1.25rem",
          }}
        >
          {!codigoEnviado ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              <p style={{ margin: 0, fontSize: "0.8rem", color: "#475569" }}>
                Enviaremos un código de seguridad de 6 dígitos a tu WhatsApp para verificar la posesión del número.
              </p>
              <button
                type="button"
                onClick={handleEnviarCodigo}
                disabled={!isPhoneValid || isSending || cooldown > 0}
                style={{
                  background: isPhoneValid && cooldown === 0 ? "#25D366" : "#94a3b8",
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "8px",
                  padding: "0.65rem 1rem",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  cursor: isPhoneValid && cooldown === 0 ? "pointer" : "not-allowed",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                  transition: "all 0.2s ease",
                }}
              >
                <span>📲</span>
                {isSending
                  ? "Enviando código..."
                  : cooldown > 0
                  ? `Reenviar en ${cooldown}s`
                  : "Enviar código por WhatsApp"}
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: "0.8rem", fontWeight: 600, color: "#0C234C" }}>
                  Ingresa el código de 6 dígitos:
                </span>
                {cooldown > 0 ? (
                  <span style={{ fontSize: "0.75rem", color: "#64748b" }}>
                    Reenviar en {cooldown}s
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={handleEnviarCodigo}
                    disabled={isSending}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#0c8de3",
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      cursor: "pointer",
                      padding: 0,
                      textDecoration: "underline",
                    }}
                  >
                    Reenviar código
                  </button>
                )}
              </div>

              <div style={{ display: "flex", gap: "0.5rem" }}>
                <InputField
                  type="text"
                  placeholder="Ej: 123456"
                  maxLength={6}
                  value={otpInput}
                  onChange={(e) => setOtpInput(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
                  style={{
                    letterSpacing: "4px",
                    fontWeight: 700,
                    fontSize: "1.1rem",
                    textAlign: "center",
                    height: "42px",
                  }}
                />
                <button
                  type="button"
                  onClick={handleVerificarCodigo}
                  disabled={otpInput.length !== 6 || isVerifying}
                  style={{
                    background: otpInput.length === 6 ? "#0c8de3" : "#cbd5e1",
                    color: "#ffffff",
                    border: "none",
                    borderRadius: "10px",
                    padding: "0 1.25rem",
                    fontWeight: 600,
                    fontSize: "0.85rem",
                    cursor: otpInput.length === 6 ? "pointer" : "not-allowed",
                    whiteSpace: "nowrap",
                  }}
                >
                  {isVerifying ? "Verificando..." : "Validar"}
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div
          style={{
            background: "#f0fdf4",
            border: "1.5px solid #86efac",
            borderRadius: "12px",
            padding: "0.75rem 1rem",
            marginBottom: "1.25rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <span style={{ color: "#16a34a", fontWeight: "bold" }}>✓</span>
            <span style={{ fontSize: "0.85rem", color: "#166534", fontWeight: 600 }}>
              Número celular verificado exitosamente
            </span>
          </div>
          <button
            type="button"
            onClick={() => {
              setFormData({
                ...formData,
                telefonoVerificado: false,
                fechaVerificacionTelefono: null,
              });
              setCodigoEnviado(false);
              setOtpInput("");
            }}
            style={{
              background: "none",
              border: "none",
              color: "#0c8de3",
              fontSize: "0.75rem",
              fontWeight: 600,
              cursor: "pointer",
              textDecoration: "underline",
              padding: 0,
            }}
          >
            Cambiar número
          </button>
        </div>
      )}

      {/* COP Opcional */}
      <FormGroup>
        <label>
          N° de Colegiatura (COP)
          <span className="optional-tag">(Opcional)</span>
        </label>
        <InputField
          type="text"
          placeholder="Ingresa tu número de colegiado"
          value={formData.cop}
          onChange={(e) => setFormData({ ...formData, cop: e.target.value })}
        />
      </FormGroup>

      <NavigationButtonsRow>
        <ActionButton type="button" onClick={onBack}>
          ← Atrás
        </ActionButton>
        <ActionButton
          type="button"
          primary
          disabled={!canProceed}
          onClick={onNext}
        >
          Siguiente →
        </ActionButton>
      </NavigationButtonsRow>
    </StepCard>
  );
};
